import React, { useContext, useEffect, useState, useMemo, useRef } from 'react';
import '../assets/scss/filters.scss';
import isEqual from 'lodash.isequal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { UserContext } from '../../../context/UserContext';
import { FormControl, InputLabel, MenuItem, Select, TextField, Button, Checkbox, ListItemText } from '@mui/material';

const OrderFilters = ({ onApplyFilters, filters, setFilters, setPagination, showAllStores }) => {
    const { user, stores, selectedStore } = useContext(UserContext);
    const isCleaning = useRef(false);


    const actualDate = new Intl.DateTimeFormat('pt-BR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).format(new Date()).split('/').reverse().join('-');

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    const formattedTomorrow = new Intl.DateTimeFormat('pt-BR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
    }).format(tomorrow).split('/').reverse().join('-');

    const initialFilters = useMemo(() => ({
        storeId: user.type === "SHOPKEEPER" ? [selectedStore._id] : stores.map((store) => store._id),
        startDate: actualDate,
        endDate: formattedTomorrow,
        deliveryCode: "",
        customerName: "",
        page: 1,
        status: "",
        groupingOnly: false
    }), [user.type, selectedStore._id, stores, actualDate, formattedTomorrow]);

    const [localFilters, setLocalFilters] = useState({ deliveryCode: filters.deliveryCode, customerName: filters.customerName });

    const isFiltered = useMemo(() => !isEqual(filters, initialFilters), [filters, initialFilters]);

    // Sincroniza o estado local quando o filtro principal é limpo
    useEffect(() => {
        if (filters.deliveryCode === '' && filters.customerName === '') {
            setLocalFilters({ deliveryCode: '', customerName: '' });
        }
    }, [filters.deliveryCode, filters.customerName]);

    useEffect(() => {
        if (isCleaning.current) {
            isCleaning.current = false;
            return;
        }

        const debounce = setTimeout(() => {
            setFilters((prev) => ({
                ...prev,
                page: 1,
                deliveryCode: localFilters.deliveryCode.trim(),
                customerName: localFilters.customerName.trim(),
            }));
            setPagination((prev) => ({ ...prev, currentPage: 1, }));
        }, 1000);

        return () => clearTimeout(debounce);
    }, [localFilters, setFilters, setPagination]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'deliveryCode' || name === 'customerName') {
            setLocalFilters(prev => ({ ...prev, [name]: value }));
        } else {
            setFilters((prev) => ({ ...prev, page: 1, [name]: value, }));
            setPagination((prev) => ({ ...prev, currentPage: 1, }));
        }
    };

    const cleanFilters = () => {
        isCleaning.current = true;
        setFilters(initialFilters);
        setLocalFilters({ deliveryCode: "", customerName: "" });
        setPagination((prev) => ({ ...prev, currentPage: 1, }));
    }

    const handleChangeStore = (event) => {
        const { value } = event.target;
        // Impede que o usuário desmarque a última loja
        if (value.length === 0) return;

        setPagination((prev) => ({
            ...prev,
            currentPage: 1,
        }));
        setFilters((prev) => ({
            ...prev,
            storeId: value,
            page: 1,
        }));
    };

    return (
        <div className="filters">
            <div className="filters-fields">
                {user.type !== "AFFILIATE" && !showAllStores &&
                <FormControl fullWidth size="small">
                    <InputLabel>Loja</InputLabel>
                    <Select
                        label="Loja"
                        multiple
                        value={filters.storeId}
                        onChange={handleChangeStore}
                        renderValue={(selected) => selected.map(id => stores.find(store => store._id === id)?.companyName).join(", ")}
                    >
                        {stores.map((store) => (
                            <MenuItem key={store._id} value={store._id}>
                                <Checkbox checked={filters.storeId.includes(store._id)} />
                                <ListItemText primary={store.companyName} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                }
                <TextField
                    label="Código do Pedido"
                    fullWidth
                    size="small"
                    name="deliveryCode"
                    value={localFilters.deliveryCode}
                    onChange={handleChange} // This now updates local state
                    placeholder="Digite o código"
                />

                <TextField
                    label="Nome do Cliente"
                    fullWidth
                    size="small"
                    name="customerName"
                    value={localFilters.customerName}
                    onChange={handleChange} // This now updates local state
                    placeholder="Digite o nome do cliente"
                />

                <TextField
                    label="Data Inicial"
                    type="date"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    name="startDate"
                    value={filters.startDate}
                    onChange={handleChange}
                />

                <TextField
                    label="Data Final"
                    type="date"
                    fullWidth
                    size="small"
                    InputLabelProps={{ shrink: true }}
                    name="endDate"
                    value={filters.endDate}
                    onChange={handleChange}
                />

            </div>

            <div className="filters-buttons">
                {isFiltered && (
                    <Button
                        variant="outlined"
                        color="error"
                        onClick={cleanFilters}
                    >
                        Limpar
                        <FontAwesomeIcon icon={faXmark} style={{ marginLeft: 5 }} />
                    </Button>
                )}
            </div>
        </div>
    );
};

export default OrderFilters;
